import type { PcmpProjectFormValues } from '../types/pcmpProjectForm.schema';
import type { PcmpProjectDetail, PcmpProjectPayload } from '../types/pcmpProject.types';
import { pcmpPriceKey } from './pcmpPriceGrid';

/** A new project, as legacy Create started it: active, on web and mobile. */
export function emptyPcmpProjectFormValues(today: string): PcmpProjectFormValues {
  return {
    name: '',
    code: '',
    description: '',
    companyId: undefined,
    bookEditionNo: undefined,
    activeFrom: today,
    activeTo: '',
    slNo: '',
    isActive: true,
    isWebVisible: true,
    isMobileVisible: true,
    categoryIds: [],
    classIds: [],
    prices: {},
  };
}

export function toPcmpProjectFormValues(project: PcmpProjectDetail): PcmpProjectFormValues {
  return {
    name: project.name,
    code: project.code ?? '',
    description: project.description ?? '',
    companyId: project.companyId,
    bookEditionNo: project.bookEditionNo ?? undefined,
    activeFrom: project.activeFrom ?? '',
    activeTo: project.activeTo ?? '',
    slNo: project.slNo === null ? '' : String(project.slNo),
    isActive: project.isActive,
    isWebVisible: project.isWebVisible,
    isMobileVisible: project.isMobileVisible,
    categoryIds: project.categories.map((category) => category.id),
    classIds: project.classes.map((academicClass) => academicClass.id),
    prices: Object.fromEntries(
      project.prices.map((price) => [
        pcmpPriceKey(price.categoryId, price.classId),
        String(price.bookPrice),
      ]),
    ),
  };
}

/**
 * The payload for create and update alike: the update sends the whole form,
 * and the API keeps unchanged grid lines. Only the cells of the chosen
 * categories × classes are sent; prices of removed rows are dropped.
 */
export function toPcmpProjectPayload(values: PcmpProjectFormValues): PcmpProjectPayload {
  // The schema has already required both.
  const companyId = values.companyId ?? 0;
  const bookEditionNo = values.bookEditionNo ?? 0;

  return {
    name: values.name.trim(),
    code: values.code.trim(),
    description: values.description.trim() || null,
    companyId,
    bookEditionNo,
    activeFrom: values.activeFrom,
    activeTo: values.activeTo,
    slNo: values.slNo.trim() === '' ? null : Number(values.slNo),
    isActive: values.isActive,
    isWebVisible: values.isWebVisible,
    isMobileVisible: values.isMobileVisible,
    prices: values.categoryIds.flatMap((categoryId) =>
      values.classIds.map((classId) => ({
        categoryId,
        classId,
        bookPrice: Number(values.prices[pcmpPriceKey(categoryId, classId)]),
      })),
    ),
  };
}
